<template lang="pug">
.page.page-writing-detail
  rail-layout(v-if="article")
    template(#side)
      router-link.back-link(:to="indexPath") ← {{ $t('writing.backToIndex') }}
      .side-meta
        span.no
          type-badge(type="essay")
        span.date {{ article.written }}
        span.period(v-if="localizedPeriod") {{ localizedPeriod }}
      outline-nav(v-if="outline.length", :title="$t('writing.outline')", :items="outline", :active-id="activeId")
      .side-related(v-if="article.related && article.related.length")
        .section-heading {{ $t('writing.related') }}
        ul
          li(v-for="(rel, i) in article.related", :key="i")
            a(v-if="rel.type === 'external' && rel.note && /^https?:/.test(rel.note)", :href="rel.note", target="_blank", rel="noopener") {{ rel.label }} ↗︎
            template(v-else)
              span.rel-label {{ rel.label }}
              span.rel-note(v-if="rel.note")  — {{ rel.note }}

    article.essay
      .locale-notice(v-if="!hasCurrentLocale") {{ localeNoticeText }}
      header.essay-header
        h1 {{ localizedTitle }}
        p.subtitle(v-if="localizedSubtitle") {{ localizedSubtitle }}
        p.excerpt(v-if="localizedExcerpt") {{ localizedExcerpt }}

      .essay-body(ref="body", v-html="localizedHtml")

      site-links(:inset="true")

      nav.essay-nav(v-if="prev || next")
        router-link.nav-prev(v-if="prev", :to="articlePath(prev.slug)")
          span.label ← {{ $t('writing.previous') }}
          span.title {{ titleFor(prev) }}
        router-link.nav-next(v-if="next", :to="articlePath(next.slug)")
          span.label {{ $t('writing.next') }} →
          span.title {{ titleFor(next) }}

  .container(v-else)
    .row.justify-content-center.pt-5
      .col-md-10.text-center
        p {{ $t('common.loading') }}
</template>

<script>
import SiteLinks from '@/components/ui/SiteLinks.vue'
import { computed, ref, nextTick, watch, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { useHead } from '@unhead/vue'
import { useActiveHeading } from '@/composables/useActiveHeading'
import { loadContent, peekContent } from '@/runtime/content-preload'
import { prefersReducedMotion } from '@/runtime/view-transition'
import indexArticles from '@/data/articles.json'

const SITE = 'https://cheyuwu.com'

export default {
  name: 'PageWritingDetail',
  components: { SiteLinks },
  setup() {
    const { t, locale } = useI18n()
    const route = useRoute()
    const article = ref(null)
    const body = ref(null)
    const { activeId, observe } = useActiveHeading('h2[id], h3[id]', body)
    const isZh = computed(() => route.path.startsWith('/zh/') || locale.value === 'zh')
    const lang = computed(() => (isZh.value ? 'zh' : 'en'))
    const pick = (v) => (v == null ? '' : typeof v === 'string' ? v : (v[lang.value] || v.en || v.zh || ''))
    const canonicalUrl = computed(() => SITE + (route.path.endsWith('/') ? route.path : route.path + '/'))
    const headTitle = computed(() => (article.value ? pick(article.value.title) : t('writing.title')))
    const headDesc = computed(() => {
      const s = article.value ? (pick(article.value.excerpt) || pick(article.value.subtitle)) : ''
      return s.length > 155 ? s.slice(0, 152) + '…' : s
    })
    const jsonLd = computed(() => {
      const a = article.value
      if (!a) return ''
      const graph = [
        {
          '@type': 'Article',
          headline: pick(a.title),
          alternativeHeadline: pick(a.subtitle) || undefined,
          description: pick(a.excerpt) || undefined,
          datePublished: a.published || a.written,
          dateModified: a.published || a.written,
          inLanguage: lang.value === 'zh' ? 'zh-Hant' : 'en',
          author: { '@type': 'Person', '@id': SITE + '/#person', name: '吳哲宇 Che-Yu Wu', url: SITE },
          publisher: { '@type': 'Person', '@id': SITE + '/#person', name: '吳哲宇 Che-Yu Wu' },
          mainEntityOfPage: canonicalUrl.value,
          url: canonicalUrl.value,
          image: route.meta?.publicEntry?.image || SITE + '/static/img/og-2026-10.jpg',
          keywords: (a.tags || []).join(', ') || undefined,
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Che-Yu Wu', item: SITE + (isZh.value ? '/zh/' : '/') },
            { '@type': 'ListItem', position: 2, name: isZh.value ? '思想' : 'Thinking', item: SITE + (isZh.value ? '/zh/thinking/' : '/thinking/') },
            { '@type': 'ListItem', position: 3, name: pick(a.title), item: canonicalUrl.value },
          ],
        },
      ]
      return JSON.stringify({ '@context': 'https://schema.org', '@graph': graph })
    })
    useHead({
      title: headTitle,
      meta: computed(() => [
        { name: 'description', content: headDesc.value },
        { property: 'og:title', content: headTitle.value },
        { property: 'og:description', content: headDesc.value },
        { property: 'og:type', content: 'article' },
        // og:image／twitter:image／尺寸由 App.vue 依 route manifest 統一輸出（含「hero 太小不用」的判斷），這裡不再另蓋一份
        ...(article.value?.published ? [{ property: 'article:published_time', content: article.value.published }] : []),
      ]),
      link: computed(() => [{ rel: 'canonical', href: canonicalUrl.value }]),
      script: computed(() => (jsonLd.value ? [{ type: 'application/ld+json', innerHTML: jsonLd.value }] : [])),
    })
    // 內文通常已在導航前載好（content-preload）：同步放進去，頁面第一次繪製就是完整文章
    const loadArticle = async (slug) => {
      const ready = peekContent('article', slug)
      article.value = ready || null
      try {
        if (!ready) article.value = await loadContent('article', slug)
        await nextTick()
        observe()
      } catch (err) {
        console.error('Article not found:', slug, err)
        article.value = null
      }
    }
    // 內文的動圖（build-articles 把 media: 參照換成 <video data-src>）：跟作品頁 loops 同一條規矩——
    // 進視窗才給 src、離開就暫停，開頁不一起下載十幾支；「減少動態」偏好下不給 src，只留海報。
    let loopIO = null
    const armLoops = () => {
      if (loopIO) { loopIO.disconnect(); loopIO = null }
      const videos = body.value ? body.value.querySelectorAll('video[data-src]') : []
      if (!videos.length || prefersReducedMotion()) return
      const show = (v) => { if (!v.getAttribute('src')) { v.src = v.dataset.src; v.load() } v.play().catch(() => {}) }
      if (typeof IntersectionObserver !== 'function') { videos.forEach(show); return }
      loopIO = new IntersectionObserver((entries) => {
        for (const e of entries) { if (e.isIntersecting) show(e.target); else if (!e.target.paused) e.target.pause() }
      }, { rootMargin: '160px 0px' })
      videos.forEach((v) => loopIO.observe(v))
    }
    // 換篇、或同一篇切中英（v-html 換掉整段內文）都要重新掛
    watch([article, lang], armLoops, { flush: 'post' })
    onBeforeUnmount(() => { if (loopIO) loopIO.disconnect() })
    return { t, locale, article, body, activeId, pick, loadArticle }
  },
  data() {
    return { indexArticles }
  },
  computed: {
    isZh() { return this.$route.path.startsWith('/zh/') || this.locale === 'zh' },
    currentLang() { return this.isZh ? 'zh' : 'en' },
    indexPath() { return this.isZh ? '/zh/thinking/' : '/thinking/' },
    hasCurrentLocale() {
      if (!this.article?.available_in) return true
      return this.article.available_in.includes(this.currentLang)
    },
    localeNoticeText() {
      return this.isZh ? '此篇尚無中文版，以英文原文呈現。' : 'No English version yet — showing the Chinese original.'
    },
    localizedTitle() { return this.pick(this.article?.title) },
    localizedSubtitle() { return this.pick(this.article?.subtitle) },
    localizedExcerpt() { return this.pick(this.article?.excerpt) },
    localizedPeriod() { return this.pick(this.article?.period) },
    localizedHtml() { return this.pick(this.article?.html) },
    outline() {
      const v = this.article?.toc
      const toc = v ? (v[this.currentLang] || v.zh || v.en || []) : []
      return toc.map(t => ({ id: t.id, label: t.level === 3 ? '·' : '§', sub: t.text.replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>') }))
    },
    localeFilteredIndex() {
      return this.indexArticles.filter(a => !a.available_in || a.available_in.includes(this.currentLang))
    },
    sortedSlugs() { return this.localeFilteredIndex.map(a => a.slug) },
    currentIndex() { return this.sortedSlugs.indexOf(this.$route.params.slug) },
    prev() {
      if (this.currentIndex <= 0) return null
      return this.localeFilteredIndex[this.currentIndex - 1]
    },
    next() {
      if (this.currentIndex < 0 || this.currentIndex >= this.localeFilteredIndex.length - 1) return null
      return this.localeFilteredIndex[this.currentIndex + 1]
    },
  },
  watch: {
    '$route.params.slug': { immediate: true, handler(slug) { if (slug) this.loadArticle(slug) } },
  },
  methods: {
    titleFor(a) { return a ? this.pick(a.title) : '' },
    articlePath(slug) { return this.isZh ? `/zh/writing/${slug}/` : `/writing/${slug}/` },
  },
}
</script>

<style lang="sass">
.page-writing-detail
  background-color: #000
  color: #fff
  min-height: 100vh
  text-align: left

  .side-related
    border-top: 1px solid var(--line)
    padding-top: 14px
    ul
      list-style: none
      padding: 0
      margin: 0
      li
        margin-bottom: 8px
        font-size: 0.88rem
        color: #aaa
        line-height: 1.5
      a
        color: #bbb
        text-decoration: none
        &:hover
          color: #fff
      .rel-label
        color: #ddd
      .rel-note
        color: #777

  .essay-header
    margin-bottom: 40px
    padding-bottom: 24px
    border-bottom: 1px solid rgba(255,255,255,0.1)
    h1
      font-family: var(--font-display)
      font-size: 2.4rem
      font-weight: 600
      line-height: 1.3
      margin-bottom: 12px
    .subtitle
      font-size: 1.15rem
      color: #aaa
      margin-bottom: 14px
    .excerpt
      font-size: 1rem
      color: #888
      line-height: 1.7
      font-style: italic
      max-width: 640px

  .essay-body
    font-size: 1.05rem
    line-height: 1.85
    color: #ddd
    h2
      font-family: var(--font-display)
      font-size: 1.5rem
      font-weight: 600
      color: #fff
      margin-top: 56px
      margin-bottom: 18px
      padding-top: 8px
      scroll-margin-top: 90px
    h3
      font-size: 1.2rem
      font-weight: 600
      color: #fff
      margin-top: 36px
      margin-bottom: 12px
      scroll-margin-top: 90px
    p
      margin-bottom: 22px
    a
      color: var(--gold)
      text-decoration: underline
      text-decoration-color: rgba(217,194,103,0.4)
      &:hover
        text-decoration-color: var(--gold)
    em.wikilink
      color: #b8a05a
      font-style: normal
      border-bottom: 1px dotted rgba(184,160,90,0.5)
      padding-bottom: 1px
    // 引言與表格的樣式由 editorial.sass 接管（.essay-body blockquote／table）
    blockquote
      p
        margin-bottom: 8px
        &:last-child
          margin-bottom: 0
      strong
        color: var(--ink)
    table
      margin: 28px 0
      font-size: 0.95rem
      th, td
        line-height: 1.6
    hr
      border: 0
      border-top: 1px solid rgba(255,255,255,0.1)
      margin: 48px 0
    hr.fn-divider
      margin-top: 64px
    sup.fn-ref a
      color: var(--gold)
      text-decoration: none
      font-size: 0.75em
      padding: 0 2px
      &:hover
        text-decoration: underline
    ol.footnotes
      font-size: 0.9rem
      color: #999
      line-height: 1.6
      padding-left: 1.4em
      li
        margin-bottom: 10px
      .fn-back
        margin-left: 6px
        color: var(--gold)
        text-decoration: none
        &:hover
          text-decoration: underline
    code
      background: rgba(255,255,255,0.06)
      padding: 2px 6px
      border-radius: 3px
      font-size: 0.9em
      color: #e8d18a
    img
      display: block
      max-width: 100%
      width: 100%
      height: auto
      margin: 36px 0 8px
      border-radius: 4px
    p > img + em
      display: block
      text-align: left
      color: #888
      font-size: 0.88rem
      font-style: italic
      line-height: 1.5
      margin-top: 6px
      margin-bottom: 24px
    // 動圖（原文的 gif，轉成無聲循環 mp4）：照原生寬度、不超過欄寬，手機縮到滿版；圖說跟圖片的 em 圖說同款
    figure.essay-loop
      margin: 36px 0 24px
      video
        display: block
        max-width: 100%
        height: auto
        border-radius: 4px
        background: #050505
      figcaption
        color: #888
        font-size: 0.88rem
        font-style: italic
        line-height: 1.5
        margin-top: 6px

  .essay-nav
    display: flex
    justify-content: space-between
    gap: 24px
    margin-top: 48px
    padding-top: 24px
    border-top: 1px solid rgba(255,255,255,0.08)
    a
      flex: 1
      display: flex
      flex-direction: column
      gap: 4px
      padding: 18px 22px
      border: 0
      border-radius: var(--r-lg)
      background: var(--bg1)
      color: var(--ink)
      text-decoration: none
      transition: var(--t-hover)
      max-width: 48%
      &:hover
        background: var(--bg2)
        text-decoration: none
        .title
          color: var(--gold)
      .label
        font-size: 0.8rem
        color: var(--ink-3)
        letter-spacing: 0.05em
        text-transform: uppercase
      .title
        font-size: 0.95rem
        line-height: 1.4
        color: var(--ink-2)
        transition: var(--t-hover)
    .nav-next
      text-align: right
      margin-left: auto

  @media (max-width: 991px)
    .essay-header h1
      font-size: 1.8rem
    .essay-nav
      flex-direction: column
      a
        max-width: 100%
</style>
